"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";

const LOCALE_TO_LANG: Record<string, string> = {
  "en-US": "en", "ru-RU": "ru", "es-MX": "es", "pt-BR": "pt",
  "fr-FR": "fr", "ar-AR": "ar", "hi-IN": "hi", "ja-JP": "ja", "ko-KR": "ko",
};

const LANG_NAMES: Record<string, { native: string; en: string }> = {
  en: { native: "English",   en: "English" },
  ru: { native: "Русский",   en: "Russian" },
  es: { native: "Español",   en: "Spanish" },
  pt: { native: "Português", en: "Portuguese" },
  fr: { native: "Français",  en: "French" },
  ar: { native: "العربية",    en: "Arabic" },
  hi: { native: "हिन्दी",     en: "Hindi" },
  ja: { native: "日本語",     en: "Japanese" },
  ko: { native: "한국어",     en: "Korean" },
};

type Props = {
  type: "terms" | "cookies" | "privacy";
  title: Record<string, string>;
  manualLabel: Record<string, string>;
  redirectLabel: Record<string, string>;
  detectedLabel: Record<string, string>;
  chooseLabel: Record<string, string>;
};

export default function LegalSelector({
  type, title, manualLabel, redirectLabel, detectedLabel, chooseLabel,
}: Props) {
  const locale = useLocale();
  const router = useRouter();
  const detectedLang = LOCALE_TO_LANG[locale] ?? "en";
  const [showSelector, setShowSelector] = useState(false);

  const sessionKey = `legal_${type}_manual`;
  const t = (m: Record<string, string>) => m[locale] ?? m["en-US"];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsSelect = params.get("select") === "1";
    const manualOverride = sessionStorage.getItem(sessionKey) === "1";

    if (wantsSelect || manualOverride) {
      setShowSelector(true);
      return;
    }

    const timer = setTimeout(() => {
      router.replace(`/${locale}/${type}/${detectedLang}`);
    }, 1500);
    return () => clearTimeout(timer);
  }, [router, detectedLang, locale, type, sessionKey]);

  const handleManualChoice = (lang: string) => {
    sessionStorage.setItem(sessionKey, "1");
    router.replace(`/${locale}/${type}/${lang}`);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <h1 className="text-2xl font-semibold text-slate-900 mb-6 text-center">{t(title)}</h1>

        {!showSelector ? (
          <div className="text-center space-y-4">
            <p className="text-sm text-slate-500">
              {t(detectedLabel)}: <span className="text-slate-900 font-medium">{LANG_NAMES[detectedLang]?.native}</span>
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              {t(redirectLabel)}
            </div>
            <button
              onClick={() => setShowSelector(true)}
              className="text-xs text-slate-400 hover:text-slate-700 underline underline-offset-2"
            >
              {t(manualLabel)}
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4 text-center">{t(chooseLabel)}</p>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(LANG_NAMES).map(([lang, names]) => (
                <button
                  key={lang}
                  onClick={() => handleManualChoice(lang)}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all text-start"
                >
                  <span className="text-sm font-medium text-slate-900">{names.native}</span>
                  <span className="text-xs text-slate-400">{names.en}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="mt-8 text-center">
          <Link href="/" className="text-xs text-slate-400 hover:text-slate-700">‹ Home</Link>
        </div>
      </div>
    </div>
  );
}